import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import {
    SingleCheckOptionItem,
    SingleOptionField,
} from '@connected/form-fields/components/option';
import { Section } from '@connected/ui/features/layouts';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useTranslation } from 'react-i18next';

import { CheckList } from '@/components/checklist';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useCondition } from '@/config/conditions';
import { queryClient } from '@/lib/query-client';
import { useOffline } from '@/stores/network-status';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/inspection-plans/plan-of-approach/',
)({
    component: RouteComponent,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: params.projectVersionId }));
    },
});

function RouteComponent() {
    const params = Route.useParams();
    const canAccessSolarInspection = useCondition('canAccessSolarInspection');
    const isUserOffline = useOffline();
    return (
        <>
            <CheckList
                title="Uit te voeren controles"
                kind="InspectionPlanTestsAndMeasurements"
                projectVersionId={params.projectVersionId}
                disabled={isUserOffline}
            />
            <CheckList
                title="Documentatie"
                kind="InspectionPlanInstallationSchematicsAndDocumentation"
                projectVersionId={params.projectVersionId}
                disabled={isUserOffline}
            />
            <CheckList
                title="Veiligheidsvoorschriften bedrijf "
                kind="InspectionPlanSafetyGuidelinesObjectLocation"
                projectVersionId={params.projectVersionId}
                disabled={isUserOffline}
            />

            {canAccessSolarInspection && (
                <>
                    <CheckList
                        title="Uitgangspunten documenten"
                        kind="Scope12InspectionPlanDocument"
                        projectVersionId={params.projectVersionId}
                        disabled={isUserOffline}
                        after={<Typology />}
                    />
                    <CheckList
                        title="Controle is uitgevoerd door middel van"
                        kind="Scope12InspectionExecution"
                        projectVersionId={params.projectVersionId}
                        disabled={isUserOffline}
                    />
                </>
            )}

            <Section>
                <Section.Footer>
                    <FooterNavigation />
                </Section.Footer>
            </Section>
        </>
    );
}

type TypologyFormValues = {
    typology: components['schemas']['ElectricalPVInstallationTypologyKind'] | null;
};

function Typology() {
    const { t } = useTranslation('inspection-plans');

    const isUserOffline = useOffline();

    const params = Route.useParams();

    const inspectionPlan = useQuery(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: params.projectVersionId }));

    const form = useForm<TypologyFormValues, ZodValidator>({
        defaultValues: {
            typology: inspectionPlan.data?.InspectionPlan?.PVInstallationTypologyKind || null,
        },
        validatorAdapter: zodValidator(),
    });

    const { patchField } = usePatcher('/api/v2/electrical/projects/inspectionplans/patch', {
        onSuccess: () => {
            queryClient.invalidateQueries(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: params.projectVersionId }));
        },
    }, {
        body: {
            ElectricalProjectVersionId: params.projectVersionId,
        },
    });

    return (
        <form.Field
            name="typology"
            validators={{
                onChange: validators.patchElectricalProjectInspectionPlanCommand.pVInstallationTypologyKind,
                onChangeAsync: patchField('PVInstallationTypologyKind'),
            }}
        >
            {({ handleChange, state, name }) => (
                <Label label={t('information.form.typology.label')}>
                    <SingleOptionField
                        name={name}
                        handleChange={handleChange}
                        state={state}
                        options={[
                            { label: 'String/centraal omvormer', value: 'StringOrCentralInverter' },
                            { label: 'Optimizers met omvormers', value: 'OptimizersWithInverters' },
                            { label: 'Micro-omvormers', value: 'MicroInverters' },
                        ]}
                        render={SingleCheckOptionItem}
                        dropdown
                        disabled={isUserOffline}
                        closeOnClick
                        placeholder="Selecteer typologie"
                    />
                    <FieldInfo state={state} />
                </Label>
            )}
        </form.Field>
    );
}
